import { useEffect, useState, useMemo } from 'react';
import { createRoute, redirect, useParams } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { Route as rootRoute } from '../__root';
import { API_BASE_URL } from '@/client/config';
import { StreamDetailView, StreamDetailData } from '@/client/components/stream-detail-view';
import { CaptionPipelineCard } from '@/client/components/caption-pipeline-card';
import { SimulcastTargetsCard } from '@/client/components/simulcast-targets-card';
import { generateStreamingURIs } from '@/client/lib/format';
import { Layout } from '@/client/layouts/layout';
import { DetailHeader } from '@/client/components/ui/detail-view';
import { Button } from '@/client/components/ui/button';
import { Loader2 } from 'lucide-react';
import type { EventFullDetailsResponse } from '@shared/types';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: 'events/$eventId',
  beforeLoad: async ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/' });
    }
  },
  component: EventDetailComponent,
});

function EventDetailComponent() {
  const { eventId } = useParams({ from: '/events/$eventId' });
  const { getAccessTokenSilently } = useAuth0();

  const [eventData, setEventData] = useState<EventFullDetailsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Parse clientID and eventID from composite eventId
  const [clientID, eventID] = useMemo(() => {
    const parts = eventId.split('_');
    if (parts.length >= 2) {
      return [parts[0], parts.slice(1).join('_')];
    }
    return [null, eventId];
  }, [eventId]);

  const fetchEventDetails = async () => {
    if (!clientID || !eventID) {
      setError('Invalid event ID');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_BASE_URL}/api/events/${clientID}/${eventID}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        setError(response.status === 404 ? 'Event not found' : 'Failed to load event details');
        setLoading(false);
        return;
      }

      const data: EventFullDetailsResponse = await response.json();
      setEventData(data);
    } catch (err) {
      console.error('Error fetching event details:', err);
      setError('Failed to load event details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEventDetails();
  }, [clientID, eventID, getAccessTokenSilently]);

  // Transform API response to StreamDetailData format
  const stream: StreamDetailData | null = useMemo(() => {
    if (!eventData) return null;

    const isLive = ['live', 'starting', 'on break', 'stopping'].includes(eventData.status);

    return {
      clientID: eventData.clientID,
      clientName: eventData.clientName,
      eventID: eventData.eventID,
      eventTitle: eventData.eventTitle,
      status: eventData.status,
      streamStatus: eventData.streamStatus,

      eventStartDateTime: eventData.eventStartDateTime,
      eventEndDateTime: eventData.eventEndDateTime,
      startDateTime: eventData.startDateTime,
      endDateTime: null,
      autoStart: eventData.autoStart,
      autoStop: eventData.autoStop,
      preroll: eventData.preroll,

      customID: eventData.customID,
      description: eventData.description,
      notes: eventData.notes,
      private: eventData.private,
      liveToBreak: eventData.liveToBreak,

      encoderID: eventData.encoderID,
      liveID: eventData.liveID,
      ingInstanceID: eventData.ingInstanceID,
      egInstanceID: eventData.egInstanceID,
      wcStreamID: eventData.wcStreamID,

      streamingURIs: clientID && eventID ? generateStreamingURIs(clientID, eventID) : null,
    };
  }, [eventData, clientID, eventID]);

  const isLiveStream = eventData ? ['live', 'starting', 'on break', 'stopping'].includes(eventData.status) : false;

  // Loading state
  if (loading && !eventData) {
    return (
      <Layout>
        <DetailHeader
          title="Loading..."
          backTo="/monitoring/caption-jobs"
          backLabel="Back"
        />
        <div className="rounded-lg border p-8 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
          <p className="text-muted-foreground mt-4">Loading event data...</p>
        </div>
      </Layout>
    );
  }

  // Error / not found
  if (error && !eventData) {
    return (
      <Layout>
        <DetailHeader
          title="Event Not Found"
          backTo="/monitoring/caption-jobs"
          backLabel="Back"
        />
        <div className="rounded-lg border p-8 text-center">
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button variant="outline" onClick={() => window.history.back()}>
            Go back
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <StreamDetailView
      stream={stream}
      loading={loading}
      backTo="/monitoring/caption-jobs"
      backLabel="Back"
      onRefresh={fetchEventDetails}
      streamId={eventId}
      isLiveStream={isLiveStream}
      hasAiCaptions={eventData?.captionPipeline !== null && eventData?.captionPipeline !== undefined}
      extraCards={
        <>
          {eventData?.captionPipeline && (
            <CaptionPipelineCard pipeline={eventData.captionPipeline} isLive={isLiveStream} />
          )}
          {eventData?.simulcastTargets && eventData.simulcastTargets.length > 0 && (
            <SimulcastTargetsCard targets={eventData.simulcastTargets} />
          )}
        </>
      }
    />
  );
}
